import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { currentLang } from '../../../utils/functions';
import { siteInfo } from '../../../utils/config';
import LogoNL from '../../../assets/img/logo/Grutto_footer.svg';
import LogoDE from '../../../assets/img/logo/KaufneKuh_footer.svg';

/**
 * Sitewide Organization + WebSite structured data.
 * Rendered once (from App). Brand/locale-aware: the same build is deployed to
 * both grutto.com (nl/uk/fr) and kaufnekuh.de (de), so every value is derived
 * from the current locale — never hardcoded to one brand.
 */
function SchemaGlobal() {
  const { t } = useTranslation();
  const lang = currentLang();
  const info = siteInfo[lang];

  if (!info) {
    return null;
  }

  const origin = window.location.origin;
  // Locale home (e.g. /nl, /de) — the stable canonical homepage. The bare root
  // geo-redirects to a locale that varies by visitor country, so it must NOT be
  // used as the entity url.
  const homeUrl = origin + '/' + lang;
  const logoUrl = origin + (lang === 'de' ? LogoDE : LogoNL);

  // Social profiles come from the per-brand contact translations.
  const sameAs = [
    t('faq.contact.facebook_url'),
    t('faq.contact.twitter_url'),
    t('faq.contact.instagram_url'),
  ].filter((url) => typeof url === 'string' && url.startsWith('http'));

  const organization = {
    '@type': 'Organization',
    '@id': origin + '/#organization',
    name: t('website_name'),
    url: homeUrl,
    logo: {
      '@type': 'ImageObject',
      url: logoUrl,
    },
  };
  if (sameAs.length) {
    organization.sameAs = sameAs;
  }

  const website = {
    '@type': 'WebSite',
    '@id': origin + '/#website',
    url: homeUrl,
    name: t('website_name'),
    inLanguage: info.lang,
    publisher: { '@id': origin + '/#organization' },
  };

  const jsonld = {
    '@context': 'https://schema.org',
    '@graph': [organization, website],
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonld)}</script>
    </Helmet>
  );
}

export default SchemaGlobal;
